<template>
  <div v-if="report" class="full">
    <div class="level hero-numbers">
      <div
        @click="selected = 'All'"
        :class="{ selected: selected === 'All'}"
        class="level-item has-text-centered hero-numbers-section">
        <score-numbers
          title="Report Card"
          icon="report"
          :failCount="failedCount(report)"
          :passCount="passCount(report)"
          :disabledCount="disabledCount(report)"
          class="big"
        ></score-numbers>
      </div>
      <div
        v-for="def in defs" :key="def.id"
        @click="selected = def.id"
        :class="{ selected: selected === def.id }"
        class="level-item has-text-centered hero-numbers-section">
        <score-numbers
          :title="def.shortTitle"
          :icon="def.icon"
          :iconSize="'40'"
          :failCount="failedCount(sectionFilter(report, def.id))"
          :passCount="passCount(sectionFilter(report, def.id))"
          :disabledCount="disabledCount(sectionFilter(report, def.id))"
        ></score-numbers>
      </div>
    </div>
    <content-nav-bar class="secondary">
      <svgicon
        name="filter"
        width="30"
        height="30"
        color="#63CCCA"
        :original="true"
        class="level-item">
      </svgicon>
      <span class="level-item">
        <span
          @click="statusFilter = 'All'"
          class="tag is-rounded level filter"
          :class="{ active: statusFilter === 'All' }">
          <svgicon
            name="circle"
            width="25"
            height="25"
            class="level-item">
          </svgicon>
          <span class="level-item">All</span>
        </span>
      </span>
      <span class="level-item">
        <span
          @click="statusFilter = 'Pass'"
          class="tag is-rounded level filter"
          :class="{ active: statusFilter === 'Pass' }">
          <svgicon
            name="ok"
            width="25"
            height="25"
            :color="passColor"
            class="level-item">
          </svgicon>
          <span class="level-item">Pass</span>
        </span>
      </span>
      <span class="level-item">
        <span
          @click="statusFilter = 'Fail'"
          class="tag is-rounded level filter"
          :class="{ active: statusFilter === 'Fail' }">
          <svgicon
            name="alert_circle"
            width="25"
            height="25"
            :color="failColor"
            class="level-item">
          </svgicon>
          <span class="level-item">Fail</span>
        </span>
      </span>
      <template slot="right">
        <div class="field level-item">
          <b-switch v-model="hideDisabled">
              Hide Disabled Controls
          </b-switch>
        </div>
      </template>
    </content-nav-bar>
    <div class="scroll-vertical controls-table full">
      <b-table
        v-if="filteredReport"
        :data="filteredReport"
        :checkable="false"
        :detailed="true"
        :showDetailIcon="true"
        detail-key="id">
        <template slot-scope="props">
          <b-table-column label="Status" centered class="status">
            <pass-fail-icon :status="props.row.pass" size="30">
            </pass-fail-icon>
          </b-table-column>
          <b-table-column label="Control">
            <svgicon
              v-if="!props.row.enabled"
              name="disabled"
              width="25"
              height="25"
              :color="disabledColor">
            </svgicon>
            <span>
              {{ props.row.label }}
            </span>
          </b-table-column>
        </template>
      </b-table>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import { mapState } from 'vuex';
import gmt from '@/mixins/gmt';
import insights from '@/mixins/insights';
import PassFailIcon from '@/components/insights/PassFailIcon.vue';
import ScoreNumbers from '@/components/insights/ScoreNumbers.vue';
import ContentNavBar from '@/components/ContentNavBar.vue';

export default {
  name: 'AssetSecurityInsights',
  mixins: [insights, gmt],
  components: { PassFailIcon, ContentNavBar, ScoreNumbers },
  props: {
    asset: {
      type: Object,
      required: true,
    },
  },
  mounted() {
    this.report = this.buildAssetReport();
  },
  data() {
    return {
      selected: 'All',
      insights: this.$typeDefs.insights,
      statusFilter: 'Fail',
      report: null,
      hideDisabled: true,
    };
  },
  computed: {
    ...mapState(['disabledControls']),
    filteredReport() {
      if (this.report === null) {
        return null;
      }
      let values = this.report;
      if (this.selected !== 'All') {
        values = _.filter(values, { sectionId: this.selected });
      }
      if (this.statusFilter === 'Pass') {
        values = _.filter(values, { pass: true });
      } else if (this.statusFilter === 'Fail') {
        values = _.filter(values, { pass: false });
      }

      if (this.hideDisabled) {
        values = _.filter(values, { enabled: true });
      }

      return values;
    },
  },
  methods: {
    sectionFilter(values, sectionId) {
      return _.filter(values, { sectionId });
    },
    passCount(values) {
      return _.filter(values, { pass: true, enabled: true }).length;
    },
    failedCount(values) {
      return _.filter(values, { pass: false, enabled: true }).length;
    },
    disabledCount(values) {
      return _.filter(values, { enabled: false }).length;
    },
  },
};
</script>

<style lang="scss" scoped>
.filter {
  cursor: pointer;
  height: 25px;
}

.active {
  background: $main-background;
}

.numbers {
  color: $tile-info-light;
  font-size: .8rem;
}

.description {
  font-weight: 500;
}

svg.level-item:not(:last-child) {
  margin: 0;
}

.status {
  width: 50px;
}

.big {
  font-size: 1.3rem;
}

.controls-table {
  margin-bottom: 1rem;
}

.hero-numbers {
  border-bottom: 1px solid $border-color;
}

.hero-numbers-section {
  cursor: pointer;
  padding: .5rem;
}

.hero-numbers-section:hover  {
  background-color: $form-subitem;
}

.selected {
  background-color: $sidebar-active;
}

.level {
  margin-bottom: 0;
}

.heading {
  font-size: 1rem;
}

.title, .score {
  color: $font-color;
  font-weight: 200;
}

.title {
  font-size: 1.5rem;
}

.score {
  font-size: 3rem;
}

</style>
